import { useState } from 'react'

import { useCommissions, useDashboard } from '@/api/queries'
import { CoinsIcon } from '@/components/icons'
import { Pager, Segmented, td, th } from '@/components/ui/Controls'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { COMMISSION_STATUS, Pill } from '@/components/ui/StatusPill'
import { describeError } from '@/lib/errors'
import { date, money, pct } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { Commission, CommissionStatus } from '@/types/api'

const PAGE_SIZE = 25

const FILTERS: Array<{ value: CommissionStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'To be settled' },
  { value: 'settled', label: 'Settled' },
  { value: 'void', label: 'Void' },
]

export function EarningsPage() {
  const [status, setStatus] = useState<CommissionStatus | ''>('')
  const [page, setPage] = useState(1)
  const query = useCommissions(status, page, PAGE_SIZE)
  const totals = useDashboard(30).data?.totals
  const rows = query.data?.data ?? []
  const info = query.data?.pageInfo

  const pick = (value: CommissionStatus | '') => {
    setStatus(value)
    setPage(1)
  }

  return (
    <>
      <PageHeader title="Earnings" description="One line per paid invoice a coupon covered. Commission = your rate × the plan's list price, fixed when it is earned." />

      <div className="mb-4 grid grid-cols-3 gap-3">
        <Figure label="To be settled" value={totals && money(totals.pendingCents)} tone="brand" />
        <Figure label="Settled" value={totals && money(totals.settledCents)} />
        <Figure label="Overall earned" value={totals && money(totals.earnedCents)} />
      </div>

      <div className="hscroll mb-3 -mx-4 px-4 sm:mx-0 sm:px-0">
        <Segmented label="Status" value={status} onChange={pick} options={FILTERS} />
      </div>

      {query.isError && !query.data ? (
        <div className="card">
          <ErrorState {...describeError(query.error, 'Could not load your earnings')} onRetry={() => void query.refetch()} />
        </div>
      ) : query.isLoading ? (
        <div className="card space-y-3 p-5">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-11" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<CoinsIcon className="h-8 w-8" />}
            title={status ? `No ${FILTERS.find((f) => f.value === status)?.label.toLowerCase()} commissions` : 'No commissions yet'}
            description={status ? 'Try another filter.' : 'Every time a customer pays with one of your codes, a line appears here with what you earned.'}
          />
        </div>
      ) : (
        <div className={cn('card overflow-hidden transition-opacity', query.isFetching && 'opacity-70')}>
          <ul className="divide-y divide-line md:hidden">
            {rows.map((c) => (
              <CommissionCard key={c.id} row={c} />
            ))}
          </ul>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full">
              <thead className="border-b border-line bg-surface-muted/60">
                <tr>
                  <th className={th}>Date</th>
                  <th className={th}>User</th>
                  <th className={th}>Coupon</th>
                  <th className={cn(th, 'text-right')}>List price</th>
                  <th className={cn(th, 'text-right')}>Discount</th>
                  <th className={cn(th, 'text-right')}>Paid</th>
                  <th className={cn(th, 'text-right')}>Rate</th>
                  <th className={cn(th, 'text-right')}>Commission</th>
                  <th className={th}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((c) => (
                  <tr key={c.id} className={cn('hover:bg-surface-muted/50', c.status === 'void' && 'text-ink-muted')}>
                    <td className={cn(td, 'text-ink-soft')}>{date(c.earnedAt)}</td>
                    <td className={td}>{c.userName || 'Unnamed user'}</td>
                    <td className={cn(td, 'num font-medium')}>{c.couponCode}</td>
                    <td className={cn(td, 'num text-right text-ink-soft')}>{money(c.grossCents)}</td>
                    <td className={cn(td, 'num text-right text-ink-soft')}>{c.discountCents ? `−${money(c.discountCents)}` : money(0)}</td>
                    <td className={cn(td, 'num text-right')}>{money(c.paidCents)}</td>
                    <td className={cn(td, 'num text-right text-ink-soft')}>{pct(c.commissionPct)}</td>
                    <td className={cn(td, 'num text-right font-semibold', c.status === 'void' ? 'text-ink-faint line-through' : 'text-brand')}>{money(c.commissionCents)}</td>
                    <td className={td}>
                      <Pill map={COMMISSION_STATUS} value={c.status} size="sm" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {info && <Pager page={info.page} totalPages={info.totalPages} totalItems={info.totalItems} pageSize={info.pageSize} onPage={setPage} />}
        </div>
      )}
    </>
  )
}

function CommissionCard({ row: c }: { row: Commission }) {
  return (
    <li className="px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[14.5px] font-medium text-ink">{c.userName || 'Unnamed user'}</p>
          <p className="text-[12.5px] text-ink-muted">
            {date(c.earnedAt)} · <span className="num text-ink-soft">{c.couponCode}</span>
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className={cn('num text-[15px] font-semibold', c.status === 'void' ? 'text-ink-faint line-through' : 'text-brand')}>{money(c.commissionCents)}</p>
          <Pill map={COMMISSION_STATUS} value={c.status} size="sm" />
        </div>
      </div>
      <p className="num mt-1.5 text-[12px] text-ink-muted">
        {money(c.grossCents)} list − {money(c.discountCents)} = {money(c.paidCents)} paid · {pct(c.commissionPct)} rate
      </p>
    </li>
  )
}

function Figure({ label, value, tone }: { label: string; value?: string; tone?: 'brand' }) {
  return (
    <div className="card min-w-0 px-3.5 py-3 sm:px-5 sm:py-4">
      <p className="truncate text-[12px] font-medium text-ink-muted sm:text-[13px]">{label}</p>
      {value == null ? <Skeleton className="mt-1.5 h-6 w-20" /> : <p className={cn('num mt-1 truncate text-[16px] font-semibold sm:text-[22px]', tone === 'brand' ? 'text-brand' : 'text-ink')}>{value}</p>}
    </div>
  )
}
